import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import { SettingsTitleWithTooltip } from 'content/pages/SettingsPage/helpers';
import { useTranslation } from 'next-i18next';
import React, { useRef, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';
import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';
import CustomMenu from 'components/MuiCustom/CustomMenu';
import CustomSwitchItem from 'components/MuiCustom/CustomSwitchItem';
import FilledTextField from 'components/MuiCustom/FilledTextField';

interface SetupDigitalInputProps {
  digitalInput: boolean;
  allowCharging: boolean;
  digitalInputLimit: number;
  digitalInput1Phase: boolean;
  digitalInputInverted: string;
  setDigitalInput: (value: boolean) => void;
  setAllowCharging: (value: boolean) => void;
  setDigitalInputLimit: (value: number) => void;
  setDigitalInput1Phase: (value: boolean) => void;
  setDigitalInputInverted: (value: string) => void;
  handleSubmitDigitalInputSetup: (
    event: React.FormEvent<HTMLFormElement>,
  ) => void;
}

const SetupDigitalInput: FC<SetupDigitalInputProps> = props => {
  const {
    digitalInput,
    allowCharging,
    digitalInputLimit,
    digitalInput1Phase,
    digitalInputInverted,
    setDigitalInput,
    setAllowCharging,
    setDigitalInputLimit,
    setDigitalInput1Phase,
    setDigitalInputInverted,
    handleSubmitDigitalInputSetup,
  } = props;

  const { t } = useTranslation();

  const [errorDigitalInputLimit, setErrorDigitalInputLimit] =
    useState<boolean>(false);

  const [dropdownAnchor, setDropdownAnchor] = useState<null | HTMLElement>(
    null,
  );
  const anchorRef = useRef<HTMLElement>(null);

  const handleCloseMenu = (): void => setDropdownAnchor(null);
  const handleOpenMenu = (): void => setDropdownAnchor(anchorRef.current);

  const handleDigitalInputLimit = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    if (!value || value < 6 || value > 16) {
      setErrorDigitalInputLimit(true);
    } else {
      setErrorDigitalInputLimit(false);
    }

    setDigitalInputLimit(value);
  };

  return (
    <ScrollContainerWrapper height="60vh">
      <Box mb={4}>
        <Typography align="center" variant="h2" sx={{ mb: 2 }}>
          {t('common:pages.device_setup.digital_input_setup.title')}
        </Typography>
        <Typography align="center" variant="body1">
          {t('common:pages.device_setup.digital_input_setup.description')}
        </Typography>
      </Box>
      <Box height="100%" sx={{ overflowY: 'auto' }}>
        <form onSubmit={handleSubmitDigitalInputSetup}>
          <CustomSwitchItem mb={4} mt={2} isSingleItem>
            <Typography align="center" variant="body1">
              {t('common:pages.device_setup.digital_input_setup.digital_input')}
            </Typography>
            <Switch
              checked={digitalInput}
              onChange={() => setDigitalInput(!digitalInput)}
            />
          </CustomSwitchItem>
          <CustomSwitchItem mb={2} isSingleItem>
            <Typography align="center" variant="body1">
              {t(
                'common:pages.device_setup.digital_input_setup.allow_charging',
              )}
            </Typography>
            <Switch
              checked={allowCharging}
              onChange={() => setAllowCharging(!allowCharging)}
            />
          </CustomSwitchItem>
          {allowCharging ? (
            <Box mb={2}>
              <FilledTextField
                label={t(
                  'common:pages.device_setup.digital_input_setup.digital_input_limit',
                )}
                name="digitalInputLimit"
                type="number"
                value={digitalInputLimit}
                onChange={handleDigitalInputLimit}
                error={errorDigitalInputLimit}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="grey.600">
                        {t('common:ampere_long')}
                      </Typography>
                    </InputAdornment>
                  ),
                  inputProps: {
                    min: 6,
                    max: 16,
                  },
                }}
                helperText={
                  errorDigitalInputLimit
                    ? t(
                        'common:pages.settings.subpages.grid.maximum_charging_current_error',
                        {
                          max: 16,
                        },
                      )
                    : undefined
                }
                fullWidth
              />
            </Box>
          ) : null}
          <CustomSwitchItem mb={2} isSingleItem>
            <Typography align="center" variant="body1">
              {t('common:pages.device_setup.digital_input_setup.phase_one')}
            </Typography>
            <Switch
              checked={digitalInput1Phase}
              onChange={() => setDigitalInput1Phase(!digitalInput1Phase)}
            />
          </CustomSwitchItem>
          <Box mb={2} ref={anchorRef}>
            <CustomListItem
              leftComponent={
                <Box display="flex" flexDirection="row">
                  <SettingsTitleWithTooltip
                    title={t(
                      'common:pages.device_setup.digital_input_setup.normal_state',
                    )}
                    tooltip={t(
                      'common:pages.device_setup.digital_input_setup.digital_input_help',
                    )}
                  />
                </Box>
              }
              isSingleItem
              rightText={t(
                `common:pages.device_setup.digital_input_setup.${digitalInputInverted}`,
              )}
              onClick={handleOpenMenu}
              rightComponent={
                <IconButton edge="end" disableRipple>
                  <UnfoldMoreRoundedIcon sx={{ color: 'grey.600' }} />
                </IconButton>
              }
            />
            <CustomMenu
              id="digital-input-inverted"
              open={Boolean(dropdownAnchor)}
              anchorEl={dropdownAnchor}
              onClose={handleCloseMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {['open', 'closed'].map(v => (
                <MenuItem
                  key={v}
                  sx={{ minWidth: 250 }}
                  selected={v === digitalInputInverted}
                  onClick={() => {
                    setDigitalInputInverted(v);
                    handleCloseMenu();
                  }}
                >
                  {t(`common:pages.device_setup.digital_input_setup.${v}`)}
                </MenuItem>
              ))}
            </CustomMenu>
          </Box>
        </form>
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SetupDigitalInput;
